import React, {useState, useRef, useEffect, useCallback} from 'react';
import styled, {ThemeProps} from 'styled-components';

import LeftArrowNewIcon from '../../../../../stories/Icons/ArrowLeftNew';
import RightArrowNewIcon from '../../../../../stories/Icons/ArrowRightNew';

import * as S from './SimilarHotels.styles';

// THEME
import {Theme} from '../../../../../stories/theme/types';

// ICONS
import LeftArrowIcon from '../../../../../stories/Icons/LeftArrowIconHome';
import RightArrowIcon from '../../../../../stories/Icons/RIghtArrowIconHome';

// COMPONENTS
import SimilarHotelCard from '../../../../../stories/components/hotels/DetailsPage/SimilarHotels/SimilarHotels';
import SimilarHotelsLoader from '../../../../../stories/components/hotels/Loadingpage/SimilarHotelsLoader/SimilarHotelsLoader';

// HOOKS
import useIntersectionObserver from '../../../../hooks/useIntersectionObserver';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

// CONFIG
import {detailsAPIEndPoints} from '../../../../config/api-config';

// UTILS
import {allFirstLevelObjectKeysAreNonNullable} from '../../../../utils/common-utils';
import {xhrHelper} from '../../../../utils/xhrHelper';
import dateformat from 'dateformat';
import {scrollToEl, detailsNavigationIds, scrollIntoViewOptions} from '../../details.config';

// SELECTORS
import {
  checkinSelectorFn,
  checkoutSelectorFn,
  roomStringSelectorFn,
  hotelsPartnerFn,
  metaPartnerSelectorFn,
} from '../../../common/redux/selectors';
import {
  cityId as cityIdSelectorFn,
  hotelIdSelector,
  countryCode as countryCodeSelectorFn,
  similarHotelsSelector,
} from '../../selectors';
import makeDetailsPageUrl from '../../../../utils/makeDetailsPageUrl';

// CONFIG
import {amenitiesMapForSimilarHotels} from '../../../../config/app-config';
import {nonNullable} from '../../../../../types/type-guards';
import useCarousel from '../../../../hooks/useCarousel';

// TYPES
import {SmoothBehaviorOptions} from 'smooth-scroll-into-view-if-needed';
import {FetchStatusType} from '../../redux/types';

type SimilarHotelResponseType = {
  pxper: number;
  sld: string;
  sb: 0 | 1;
  is_aa: 0 | 1;
  bt: string;
  fcp: string;
  ut: string;
  ptl: Array<{
    ic: string;
    t: string;
    img: Array<any>;
  }>;
  cbm: string;
  cot: string;
  ofs: string;
  pph: string;
  opr: number;
  pm: number;
  spr: number;
  dtc: number;
  ti: number;
  gseg: string;
  adp: number;
  tax: number;
  ami: number[];
  oi: number[];
  tgi: number[];
  hti: number[];
  nbi: number[];
  aci: number[];
  ch: string;
  chc: string;
  hc: string;
  hn: string;
  ibp: string;
  l: string;
  t: string;
  rtn: string;
  la: number;
  lo: number;
  gr: number;
  gos_flag: 0 | 1;
  pr: number; // 0 | 1 ?
  hr: number;
  grc: number;
  gri: number;
  grti: number;
  vi: string;
  hfe: string;
  hmd: string;
  gsv: string;
  emi: string; // number in a string
  emid: {
    bas: number;
    tax: number;
    fin: number;
    e3: number;
    e6: number;
    e9: number;
  };
  ad: Array<any>;
  gtv: string;
};

export type SimilarHotelsAPIResponseTypes = {
  data: Array<SimilarHotelResponseType>;
  city_meta_info: {
    // "pxgrn": number;
    // "altpx": string;
    // "pot": Array<{
    //   "oi": string, // a number in a string
    //   "ot": string;
    // }>;
    // "tot": Array<{
    //   "oi": string; // a number in a string
    //   "ot": string;
    // }>;
    // "sb_msg": string;
    // "sb": number;
    // "banner_type": string;
    // "scthr": number;
    // "is_ipl": 0 | 1,
    // "collections": Array<{
    //   "img": string;
    //   "slug": string;
    //   "st": string;
    //   "t": string;
    //   "desc": string;
    // }>
    // "segments": Array<{
    //   "img": string;
    //   "slug": string;
    //   "st": string;
    //   "t": string;
    //   "desc": string;
    // }>;
    // "avai_color": string[]; // hex codes
    // "am": string[];
    // "ata": string[];
    // "aty": string[];
    // "grt": string[];
    // "tabs": string[];
    ac: string[];
    // "dsl": Array<{
    //   "n": string;
    //   "ahc": number;
    // }>;
    // "amc": {
    //   [k: string]: number;
    // };
    // "tyhc": {
    //   [k: string]: number;
    // };
    // "lli": {
    //   [k: string]: string;
    // };
    // "lgdin": number;
    // "ti": number;
    // "ct": string;
    // "co": string;
    // "coc": string;
    // "pax": string;
    // "gos_msg": string;
    // "vcid": string;
    // "la": number;
    // "lo": number;
    // "pid_max": number,
    // "vcids": string[]
    // "vcidsn": string[]
  };
};

const defaultAPIData: SimilarHotelsAPIResponseTypes = {
  data: [],
  city_meta_info: {
    ac: [],
  },
};

export type OptionsType = {
  checkinYYYYMMDD: string;
  checkoutYYYYMMDD: string;
  pax: string;
  cityId: string;
  hotelCode: string;
};

const fetchSimilarHotels = async (options: OptionsType, setIsLoadingState) => {
  const {checkinYYYYMMDD, checkoutYYYYMMDD, pax, cityId, hotelCode} = options;

  if (!allFirstLevelObjectKeysAreNonNullable(options)) {
    return [];
  }

  /** copied from old fore code */
  const filter = {
    gosuggest: [
      {
        vhid: hotelCode,
        type: 0,
      },
    ],
  };

  /** copied from old fore code */
  const queryString = `?&cur=INR&f=${JSON.stringify(filter)}&pid=0&s=popularity`;

  // const strQry = `hotels-${cityId}-${checkinYYYYMMDD}-${checkoutYYYYMMDD}-${pax}`;

  const url = detailsAPIEndPoints.similarHotels({
    cityId,
    checkin: checkinYYYYMMDD,
    checkout: checkoutYYYYMMDD,
    hyphenatedPax: pax,
    queryString,
  });

  // console.log(url)

  let similarHotelsData: SimilarHotelsAPIResponseTypes | null = null;
  let error: null | Error = null;

  try {
    const response = await xhrHelper<null, SimilarHotelsAPIResponseTypes>({
      name: 'similarHotels',
      type: 'GET',
      url,
    });

    if (response.data) {
      similarHotelsData = (response.data && response.data) || defaultAPIData;
      setIsLoadingState('RESOLVED');
    }
  } catch (e) {
    error = e;
    setIsLoadingState('REJECTED');
  }

  return [similarHotelsData, error] as [SimilarHotelsAPIResponseTypes, Error];
};

function useCallbackRef() {
  const ref = useRef<HTMLElement | null>(null);
  const setRef = useCallback((node: HTMLElement) => {
    if (ref.current) {
      // Make sure to cleanup any events/references added to the last instance
    }

    if (node) {
      // Check if a node is actually passed. Otherwise node would be null.
      // You can now do what you need to, addEventListeners, measure, etc.
    }

    // Save a reference to the node
    ref.current = node;
  }, []);

  let ret: [React.MutableRefObject<HTMLElement | null>, (node: HTMLElement) => void] = [
    ref,
    setRef,
  ];
  return ret;
}

const getScrollIntoViewOptions = (
  similarHotelsContainerRef: HTMLElement,
): Partial<SmoothBehaviorOptions> => ({
  inline: 'start',
  block: 'start',
  boundary: similarHotelsContainerRef,
});

function getSimilarHotelsUIProps({
  hotel,
  checkinJSDate,
  checkoutJSDate,
  roomString,
  countryCode,
  cityName,
  index,
  amenitiesList = [],
  currentHotel,
  partner,
  metaPartner = '',
  cityMetaInfo,
}) {
  const {am: amenitiesListMapping} = cityMetaInfo;

  const similarHotelsUIProps = {
    imageSrc: hotel.t,
    hotelName: hotel.hn,
    hotelCode: hotel.hc,
    hotelRating: hotel.hr,
    hotelLocation: hotel.l,

    userRating: hotel.gr,
    userRatingSource: hotel.gri,
    noOfReviews: hotel.grc,
    coupleFriendly: !!hotel.cf,
    payAtHotel: !!hotel.pah,
    slashedPrice: hotel.opr,
    showPrice: hotel.spr,
    vendor: hotel.ibp,
    hmd: hotel.hmd,

    checkin: checkinJSDate,
    checkout: checkoutJSDate,
    pax: roomString,
    countryCode,
    cityName,
    index: index,
    currentHotel,

    amenitiesList,

    persuasions: {
      navigateToDetailsPage: () => {},
      roomTypeName: hotel.rtn,
      belowBannerPersuasion: hotel.bbp
    },

    onClick(e: React.MouseEvent<HTMLElement>) {
      e.preventDefault();
      const detailMakingOptions = {
        hotelData: {
          hn: similarHotelsUIProps.hotelName,
          hc: similarHotelsUIProps.hotelCode,
          ibp: similarHotelsUIProps.vendor,
          hmd: similarHotelsUIProps.hmd,
        },
        partner: partner,
        checkin: {
          date: checkinJSDate,
        },
        checkout: {
          date: checkoutJSDate,
        },
        roomString: roomString,
        cityMetaInfo: {
          coc: countryCode,
        },
        destination: {
          n: cityName,
        },
        metaPartner,
      };

      const detailsUrlForSimilarHotel = makeDetailsPageUrl(detailMakingOptions);

      window.open(detailsUrlForSimilarHotel, '_blank');
    },
  };

  return similarHotelsUIProps;
}

const SimilarHotels = () => {
  const similarHotelsData = useSelector(similarHotelsSelector);

  const {list, cityMetaInfo, fetchStatus} = similarHotelsData;

  const currentHotel = !!list.length && list[0];

  const similarHotels = list.slice(1);

  // no similar hotels came from data
  if (!similarHotels.length && fetchStatus === FetchStatusType.RESOLVED) {
    return null;
  }

  // api call errored out
  if (fetchStatus === FetchStatusType.REJECTED) {
    return null;
  }

  if (!similarHotels.length || fetchStatus === FetchStatusType.PENDING) {
    return <SimilarHotelsLoaderComp />;
  }

  return (
    <SimilarHotelsUI
      similarHotels={similarHotels}
      currentHotel={currentHotel}
      cityMetaInfo={cityMetaInfo}
    />
  );
};


const SimilarHotelsUI = ({similarHotels, currentHotel, cityMetaInfo}) => {
  let [cardsContainerRef, setCardsContainerRef] = useCallbackRef();

  const checkinJSDate = useSelector(checkinSelectorFn).date;
  const checkoutJSDate = useSelector(checkoutSelectorFn).date;
  const roomString = useSelector(roomStringSelectorFn);
  const partnerFromStore = useSelector(hotelsPartnerFn);

  const countryCode = useSelector(countryCodeSelectorFn);

  const {cityName = ''} = useParams<{cityName: string}>();

  const {
    increment,
    decrement,
    leftCursor,
    rightCursor,
    isLeftCursorActive,
    isRightCursorActive,
  } = useCarousel(similarHotels, 3);

  const metaPartner = useSelector(metaPartnerSelectorFn);

  const currentHotelAmenitiesList = (currentHotel.aci || [])
    .map(amenityCode => {
      return amenitiesMapForSimilarHotels[cityMetaInfo?.ac?.[amenityCode]];
    })
    .filter(nonNullable);

  const similarHotelsUIPropsForCurrentHotel = React.useMemo(() => {
    return getSimilarHotelsUIProps({
      hotel: currentHotel,
      checkinJSDate,
      checkoutJSDate,
      roomString,
      countryCode,
      cityName,
      index: -1,
      amenitiesList: currentHotelAmenitiesList,
      currentHotel: true,
      partner: partnerFromStore,
      metaPartner,
      cityMetaInfo,
    });
  }, [
    currentHotel,
    checkinJSDate,
    checkoutJSDate,
    roomString,
    countryCode,
    cityName,
    currentHotelAmenitiesList,
    partnerFromStore,
    metaPartner,
  ]);

  return (
    <S.DetailSimilarOuterWrap id={detailsNavigationIds.similarHotels}>
      <S.SimilarHotelsInnerWrap>
          <S.SimilarHotelsHeadWrap>
              <S.SimilarInnerHeadWrap>
                  <S.SimilarHotelsHeadText>Compare Similar hotels</S.SimilarHotelsHeadText>
                  <S.SimilarHotelsHeadSubText>Important information that you need to know before you book!</S.SimilarHotelsHeadSubText>
              </S.SimilarInnerHeadWrap>
              {similarHotels.length > 3 && (
                <S.SimilarCaroIconWrap>
                  <S.IconInnerWrapCaro>
                    <S.LeftIconWrapCaro onClick={() => {
                      try {
                        scrollToEl(
                          `#similar-hotels-${leftCursor}`,
                          getScrollIntoViewOptions(cardsContainerRef.current),
                        );
                      } catch (e) {
                        console.log(`#similar-hotels-${leftCursor} element might not be present`);
                        console.log(e);
                      }

                      decrement();
                    }}>
                      <LeftArrowNewIcon width="1.3rem" height="1.3rem" isactive={isLeftCursorActive ? 'true' : 'false'} />
                    </S.LeftIconWrapCaro>

                    <S.RightIconWrapCaro onClick={() => {
                      increment();

                      try {
                        scrollToEl(
                          `#similar-hotels-${rightCursor - 1}`,
                          getScrollIntoViewOptions(cardsContainerRef.current),
                        );
                      } catch (e) {
                        console.log(`#similar-hotels-${rightCursor - 1} element might not be present`);
                        console.log(e);
                      }
                    }}>
                      <RightArrowNewIcon width="1.3rem" height="1.3rem" isactive={isRightCursorActive ? 'true' : 'false'} />
                    </S.RightIconWrapCaro>
                  </S.IconInnerWrapCaro>
                </S.SimilarCaroIconWrap>
              )}
          </S.SimilarHotelsHeadWrap>
          <S.SmiliarHotelsMainBlock>
              <S.SimilarHotelsCaroBlock>
                  <S.SelectedHotelBlock>
                      <S.SelectedHotelInfoBlock>
                          <S.CaroInfoHeadWrap>
                              <S.CaroBigHeadWrap>
                                <S.CaroDealText>User rating</S.CaroDealText>
                              </S.CaroBigHeadWrap>
                              <S.CaroSmallHeadWrap>
                                <S.CaroDealText>Location</S.CaroDealText>
                              </S.CaroSmallHeadWrap>
                              <S.CaroBigHeadWrap>
                                <S.CaroDealText>Cancellation</S.CaroDealText>
                              </S.CaroBigHeadWrap>
                              <S.CaroSmallHeadWrap>
                                <S.CaroDealText>Room Type</S.CaroDealText>
                              </S.CaroSmallHeadWrap>
                          </S.CaroInfoHeadWrap>
                          
                          {!!similarHotels.length && (
                            <SimilarHotelCard
                              key={currentHotel.hc}
                              id="similar-hotels-current-hotel"
                              className="similar-hotel-card"
                              {...similarHotelsUIPropsForCurrentHotel}
                            />
                          )}
                      </S.SelectedHotelInfoBlock>
                  </S.SelectedHotelBlock>
                  <S.SimilarHotelOtherCaro>
                    <S.SimilarOtherCard>
                      {!!similarHotels.length &&
                      similarHotels.map((similarHotel, i) => {
                        const amenitiesList = similarHotel.aci
                          .map(amenityCode => {
                            return amenitiesMapForSimilarHotels[cityMetaInfo?.ac?.[amenityCode]];
                          })
                          .filter(nonNullable);

                        const similarHotelsUIProps = getSimilarHotelsUIProps({
                          hotel: similarHotel,
                          checkinJSDate,
                          checkoutJSDate,
                          roomString,
                          countryCode,
                          cityName,
                          index: i,
                          amenitiesList: currentHotelAmenitiesList,
                          currentHotel: false,
                          partner: partnerFromStore,
                          cityMetaInfo,
                        });

                        return (
                          <SimilarHotelCard
                            key={similarHotel.hc}
                            id={`similar-hotels-${i + 1}`}
                            className="similar-hotel-card"
                            {...similarHotelsUIProps}
                          />
                        );
                      })}
                    </S.SimilarOtherCard>
                  </S.SimilarHotelOtherCaro>
              </S.SimilarHotelsCaroBlock>
          </S.SmiliarHotelsMainBlock>
      </S.SimilarHotelsInnerWrap>
    </S.DetailSimilarOuterWrap>
  );
};

const SimilarHotelsLoaderComp = () => (
  <S.DetailSimilarOuterWrap>
    <SimilarHotelsLoader />
    <SimilarHotelsLoader />
    <SimilarHotelsLoader />
    <SimilarHotelsLoader />
  </S.DetailSimilarOuterWrap>
);
export default SimilarHotels;
